import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import SlidePreviewBody from "@/components/SlidePreviewBody";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Eagerly warm every lazy slide-preview chunk the moment the print route's
   bundle loads — webpack dedupes these against SlidePreviewBody's React.lazy
   imports, so by the time the slide renders the lazy components resolve
   instantly and the white loading placeholder is never captured. */
const CHUNKS_PROMISE = Promise.all([
  import("@/components/slidePreviews/FinancialProjectionsPreview.jsx"),
  import("@/components/slidePreviews/CoverPreview.jsx"),
  import("@/components/slidePreviews/MarketSizePreview.jsx"),
  import("@/components/slidePreviews/BusinessModelPreview.jsx"),
  import("@/components/slidePreviews/TeamPreview.jsx"),
  import("@/components/slidePreviews/CapitalAskPreview.jsx"),
  import("@/components/slidePreviews/CompetitivePreview.jsx"),
]).catch(() => {});

/**
 * /deck-print — internal print-rendering route used by the backend
 * Playwright PDF exporter (backend/pitch_playwright_export.py).
 *
 * Renders ONE slide of a deck full-screen at a fixed 16:9 ratio.
 * Query params: ?deck=<deck_id>&slide=<0-based index>&token=<session token>
 *
 * Contract with the exporter:
 *  - window.__PRINT_TOTAL__  = total item count (slides + sub-slides)
 *  - body[data-print-ready="1"] set ONLY after fonts + images are loaded
 *  - @page is 13.333in × 7.5in with zero margin, matching the 1280×720
 *    viewport (96 CSS px/in) so page.pdf() captures a full-bleed slide.
 */

// Mirrors PlayDeckModal's queue-building rule exactly: skip slide 7,
// flatten extras as sub-slides that inherit parent images/design.
function buildItems(deck) {
  const sorted = (deck?.slides || [])
    .filter((s) => s && s.num !== 7)
    .slice()
    .sort((a, b) => a.num - b.num);
  const items = [];
  for (const s of sorted) {
    items.push({ slide: s });
    (s.extras || []).forEach((ex) => {
      const exImages = (ex.images && ex.images.length > 0) ? ex.images : (s.images || []);
      items.push({
        slide: {
          ...s,
          title: ex.title || `${s.title} (continued)`,
          content: ex.content || "",
          design_prefs: ex.design_prefs || s.design_prefs || {},
          images: exImages,
          _extraId: ex.id,
        },
      });
    });
  }
  return items;
}

export default function DeckPrint() {
  const [params] = useSearchParams();
  const deckId = params.get("deck");
  const slideIdx = parseInt(params.get("slide") || "0", 10);
  const token = params.get("token");
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState(null);
  const [chunksReady, setChunksReady] = useState(false);

  useEffect(() => {
    let alive = true;
    CHUNKS_PROMISE.then(() => { if (alive) setChunksReady(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!deckId || !token) { setError("Missing deck or token"); return; }
    axios.get(`${API}/pitch/decks/${deckId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setDeck(r.data))
      .catch((e) => setError(e?.response?.data?.detail || String(e)));
  }, [deckId, token]);

  const items = useMemo(() => (deck ? buildItems(deck) : []), [deck]);
  const item = items[Math.min(Math.max(slideIdx, 0), Math.max(items.length - 1, 0))];

  // Readiness signal — the flag is set ONLY once (a) the REAL slide component
  // is mounted (no Suspense loading placeholder in the DOM) and (b) every
  // image is fully loaded AND decoded. No fixed delays.
  useEffect(() => {
    if (!deck || !item || !chunksReady) return;
    window.__PRINT_TOTAL__ = items.length;
    let cancelled = false;
    const nextFrame = () => new Promise((res) => requestAnimationFrame(() => res()));

    const settleImages = async () => {
      const imgs = Array.from(document.images || []);
      await Promise.all(imgs.map(async (img) => {
        if (!img.complete) {
          await new Promise((res) => {
            img.addEventListener("load", res, { once: true });
            img.addEventListener("error", res, { once: true });
          });
        }
        if (img.complete && img.naturalWidth > 2) {
          try { await img.decode(); } catch { /* already decoded or unsupported */ }
        }
      }));
      return imgs.length;
    };

    const realSlideMounted = () => {
      const stage = document.getElementById("deck-print-stage");
      return !!(stage && stage.childElementCount > 0)
        && !document.querySelector('[data-print-placeholder="1"]');
    };

    const signal = async () => {
      try { await document.fonts.ready; } catch { /* older engines */ }
      const started = Date.now();
      while (!cancelled) {
        if (realSlideMounted()) {
          const count = await settleImages();
          await nextFrame();
          // Stable when no placeholder re-appeared and no new images mounted
          // during the decode pass (e.g. photos inside a just-resolved chunk).
          if (realSlideMounted() && Array.from(document.images || []).length === count) break;
        }
        if (Date.now() - started > 20000) break; // hard cap — never hang the exporter
        await nextFrame();
      }
      if (!cancelled) document.body.setAttribute("data-print-ready", "1");
    };
    signal();
    return () => { cancelled = true; document.body.removeAttribute("data-print-ready"); };
  }, [deck, item, items.length, chunksReady]);

  useEffect(() => {
    if (error) {
      // Surface failure to the exporter so it doesn't wait for the timeout.
      window.__PRINT_ERROR__ = String(error);
      document.body.setAttribute("data-print-error", "1");
    }
  }, [error]);

  const bullets = ((item?.slide?.content) || "")
    .split("\n")
    .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
    .filter(Boolean);
  const primary = deck?.brand_color || "#0A8B85";
  const layout = ((item?.slide?.design_prefs || {}).layout || "").trim();

  return (
    <>
      <style>{`
        @page { size: 13.333in 7.5in; margin: 0; }
        html, body { margin: 0 !important; padding: 0 !important; background: #fff; }
        #deck-print-stage { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>
      <div
        id="deck-print-stage"
        data-testid="deck-print-stage"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "56.25vw",     /* fixed 16:9 — 720px at the 1280px viewport */
          maxHeight: "100vh",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        {error ? (
          <div style={{ padding: 24, fontFamily: "monospace", fontSize: 12 }}>{String(error)}</div>
        ) : deck && item && chunksReady ? (
          <SlidePreviewBody deck={deck} slide={item.slide} bullets={bullets}
            primary={primary} layout={layout} />
        ) : null}
      </div>
    </>
  );
}
